import * as React from 'react';

import { isAuthenticatedUser } from 'js/lib/user';

import { useTracker } from '@coursera/event-pulse/react';

import { getShouldShowCourseraPlusMerchandisingPlacements } from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import { useCourseraPlusProductOwnerships } from 'bundles/coursera-plus-common/utils/withCourseraPlusProductOwnerships';
import { useRelatedRoles } from 'bundles/unified-career-academy/contexts/ConsumerPathwayRelatedRolesContext';
import type { ConsumerPathwayRolePageDataContextType } from 'bundles/unified-career-academy/contexts/ConsumerPathwayRolePageDataContext';
import CareerRoleCard from 'bundles/unified-common/components/CareerRoleCard';
import CourseraPlusUpsellCard from 'bundles/unified-common/components/coursera-plus-upsells/CourseraPlusUpsellCard';
import CareerAcademyModule from 'bundles/unified-common/modules/CareerAcademyModule';
import { useCourseraPlusUpsellPlacementData } from 'bundles/unified-common/providers/CourseraPlusUpsellPlacementDataProvider';

import _t from 'i18n!nls/career-academy';

type Props = {
  pageData: ConsumerPathwayRolePageDataContextType;
};

export const RelatedRolesModuleRenderer = ({ pageData }: Props) => {
  const { relatedCareerRoles } = useRelatedRoles() || {};
  const track = useTracker();
  const { ownsCourseraLite, ownsCourseraPlus } = useCourseraPlusProductOwnerships();
  const { upsellPlacement } = useCourseraPlusUpsellPlacementData();

  if (!relatedCareerRoles || !relatedCareerRoles.entities?.length) {
    return null;
  }

  const userHasSubscription = ownsCourseraLite || ownsCourseraPlus;
  let displayExperimentChanges = false;
  if (!userHasSubscription) {
    displayExperimentChanges = getShouldShowCourseraPlusMerchandisingPlacements();
  }

  const trackCtaClick = () => {
    track('click_button', {
      button: {
        name: 'career_academy_cta',
        linkName: 'explore_all_roles',
        linkType: 'internal',
        linkURL: '/career-academy',
      },
    });
  };

  let upsellCards: React.ComponentProps<typeof CareerAcademyModule>['upsellCards'];

  if (displayExperimentChanges && (upsellPlacement || !isAuthenticatedUser())) {
    upsellCards = [
      {
        id: '1',
        renderComponent: (props: { index: number }) => (
          <CourseraPlusUpsellCard
            title={_t('Find your dream career by exploring them all.')}
            mobileBreakpoint="xs"
            eventingData={{ index: props.index, modulePlacement: upsellPlacement }}
          />
        ),
        position: 3,
      },
    ];
  }

  const relatedCareerRolesToUse = relatedCareerRoles;

  if (upsellCards?.length && relatedCareerRolesToUse?.entities?.length === 4) {
    relatedCareerRolesToUse.entities.pop();
  }

  return (
    <div data-testid="related-roles-module">
      <CareerAcademyModule
        headline={
          pageData?.careerRoleData?.name
            ? _t('Roles similar to #{roleName}', { roleName: pageData?.careerRoleData?.name })
            : _t('Similar roles')
        }
        collection={relatedCareerRolesToUse}
        cta={{
          text: _t('View all roles'),
          target: '/career-academy',
          variant: 'ghost',
          onClick: trackCtaClick,
        }}
        ProductCardComponent={CareerRoleCard}
        layout="text-top/image-bottom"
        backgroundColor="grey"
        numOfEntriesPerCollection={4}
        defaultMobileRows={4}
        showAllEntries
        upsellCards={upsellCards}
      />
    </div>
  );
};

export default RelatedRolesModuleRenderer;
